// R-COOL-VALIDITY — BTM cooling-selection viability (single source of truth).
//
// The cooling selectors (heat-rejection sink, BTM cooling-generation machine,
// IT-side cooling strategy) historically offered the FULL Cartesian product with
// NO physical-viability gating. That let a grid-only or solar+BESS project pick
// `absorption-thermal` / `steam-driven-mechanical` — physically impossible,
// because those machines are DRIVEN by an on-site thermal/steam stream and a
// renewables-or-grid mix has no on-site waste heat to drive them.
//
// This module is the ONE place that answers "is this cooling option viable for
// this power mix?" so every UI (ProjectIntentForm, GenerateSimulationWizard, …)
// filters/disables uniformly and identically.
//
// SCOPE / non-goals:
//   - Pure planning logic. NO CoolProp / IAPWS / backend imports (mirrors the
//     conventions of `phaseFeasibility.ts`). It reasons about declared CONFIG
//     (power mix + an optional water signal), not thermodynamic state.
//   - It MIRRORS — does NOT re-derive — powerStack.ts's on-site-thermal
//     predicate (the same key sets). It deliberately does NOT import or refactor
//     powerStack.ts (which carries PUE-parity risk); the key sets are duplicated
//     here with a test that pins them to powerStack's behaviour.
//
// UNITS: none (boolean viability over enum vocabularies). No CoolProp boundary.

import type {
  BopHeatRejection,
  CoolingGeneration,
  PhasePowerComponentKey,
  PowerSourceKey,
  ProjectIntent,
  ProjectPhase,
} from '../../types/siteRag';
import { isCoolingPairContradictory } from '../../types/siteRag';
import { effectivePhases } from '../projectIntent';

// ─────────────────────────────────────────────────────────────────────────────
// ON-SITE WASTE-HEAT PREDICATE — mirrors powerStack.ts EXACTLY.
//
// These two key sets are a deliberate MIRROR of the private
// ON_SITE_THERMAL_SOURCE_KEYS / ON_SITE_THERMAL_COMPONENT_KEYS in
// `src/lib/powerStack.ts` (~L130-146). powerStack.ts does not export them and we
// do not want to widen its surface (PUE-parity risk), so they are duplicated
// here. `coolingValidity.test.ts` pins this predicate to powerStack's resolved
// PUE behaviour so the two can never silently diverge.
//
// On-site THERMAL generation = combustion or fission with a high-grade heat
// stream that can drive absorption / steam-driven cooling. Grid import, solar
// +BESS and wind+BESS carry NO usable on-site heat.
// ─────────────────────────────────────────────────────────────────────────────

/** Coarse `PowerSourceKey`s that denote on-site thermal generation. */
const ON_SITE_THERMAL_SOURCE_KEYS: ReadonlySet<PowerSourceKey> = new Set([
  'gas-cc',
  'gas-sc',
  'smr',
  'large-nuclear',
]);

/** Granular `PhasePowerComponentKey`s that denote on-site thermal generation.
 *  `recip` / `gas-turbine` are the granular forms of gas-sc / gas-cc. */
const ON_SITE_THERMAL_COMPONENT_KEYS: ReadonlySet<PhasePowerComponentKey> = new Set([
  'recip',
  'gas-turbine',
  'smr',
  'large-nuclear',
]);

/**
 * The power-mix + environment context a viability check reasons over.
 *
 * Either supply the granular `sources` / `components` arrays directly, or build
 * the context from an intent with {@link coolingContextFromIntent} (the form +
 * wizard path). Both are optional so a partially-drafted intent still resolves
 * (empty mix → no waste heat → thermally-driven options gate off).
 */
export interface CoolingContext {
  /** Coarse per-phase power sources present in the mix (any phase). */
  sources?: ReadonlyArray<PowerSourceKey>;
  /** Granular per-phase power components present in the mix (any phase). */
  components?: ReadonlyArray<PhasePowerComponentKey>;
  /**
   * OPTIONAL water-stress signal. `true` = a CLEAR water-stress/scarcity signal
   * exists for the site (e.g. high WRI Aqueduct baseline water stress). Absent /
   * `undefined` = no signal (the common case) → evaporative / once-through
   * rejection is WARNED, not hard-disabled. `false` = explicitly water-abundant.
   */
  waterStressed?: boolean;
  /**
   * OPTIONAL sibling cold-PRODUCTION machine currently selected on the
   * `coolingGeneration` axis. Lets the HEAT-REJECTION gate disable the thermal
   * `absorption-cchp` SINK when the chosen MACHINE is electric / non-thermal
   * (cross-field consistency — the absorption sink IS the thermal machine in
   * disguise, so it can ONLY coexist with a thermal machine). Absent → the
   * cross-field rule is a no-op (back-compat for callers that only carry the
   * power mix), so partial-intent callers are never broken.
   */
  coolingGeneration?: CoolingGeneration;
  /**
   * OPTIONAL sibling cold-END sink currently selected on the `bopHeatRejection`
   * axis. Lets the GENERATION gate disable electric / non-thermal MACHINES when
   * the chosen SINK is the thermal `absorption-cchp` (the mirror of the above).
   * Absent → the cross-field rule is a no-op (back-compat).
   */
  bopHeatRejection?: BopHeatRejection;
  /**
   * P3-6 — explicit waste-heat availability signal, INDEPENDENT of the power
   * mix. `true` = a declared waste-heat offtake (intent `wasteHeatOfftake`) OR
   * on-site thermal-load / waste-heat EVIDENCE confirms usable heat. When set,
   * `wasteHeatAvailable` short-circuits to `true` regardless of the power-mix
   * predicate, so a thermally-driven cooling machine / absorption sink is not
   * falsely blocked on a site whose thermal source is declared/evidenced rather
   * than inferable from the coarse mix. Absent/undefined → the predicate falls
   * back to the power-mix inference (back-compat, byte-stable for legacy ctx).
   */
  wasteHeatDeclared?: boolean;
}

/** Shared, machine-agnostic reason string for the no-waste-heat gate. */
const NO_WASTE_HEAT_REASON =
  'needs on-site waste heat (gas turbine / recip / SMR / nuclear); none in this power mix';

/** Shared advisory for water-intensive rejection when no water signal exists. */
const WATER_WARN_REASON =
  'verify water availability — evaporative / once-through rejection is water-intensive';

/** Shared hard-block reason when a clear water-stress signal exists. */
const WATER_STRESS_REASON =
  'site shows water stress — avoid evaporative / once-through rejection';

/** Cross-field reason — a thermal `absorption-cchp` SINK can only coexist with a
 *  thermal cold-production MACHINE (absorption / steam). Shown when an electric /
 *  non-thermal machine is selected. */
const CONTRADICTORY_MACHINE_REASON =
  'incompatible with the thermal absorption / CCHP cold end — that sink IS the absorption machine; pick absorption (thermal) here, or change the cold end';

/** Cross-field reason — the thermal `absorption-cchp` SINK is impossible when the
 *  cold-production MACHINE is electric / non-thermal. */
const CONTRADICTORY_SINK_REASON =
  'incompatible with the selected electric / non-thermal cold-production machine — change the cooling machine to absorption (thermal), or pick a non-thermal cold end';

/**
 * TRUE when the power mix has an on-site thermal source that can drive
 * thermally-driven (absorption / steam) cooling. MIRRORS powerStack.ts's
 * predicate exactly: on-site thermal SOURCE keys ∪ granular COMPONENT keys.
 *
 * Null/empty context → false (conservative: no declared heat source ⇒ no waste
 * heat). A `hybrid` coarse source carries no signal of its own — it is resolved
 * through the granular `components` (same as powerStack inspects
 * `powerSourceConfig.components`).
 */
export function wasteHeatAvailable(ctx: CoolingContext | undefined): boolean {
  if (!ctx) return false;
  // P3-6 — an explicitly DECLARED waste-heat offtake (intent) or on-site
  // thermal-load EVIDENCE confirms usable heat regardless of what the coarse
  // power-mix predicate can infer. Short-circuit on the declared/evidenced
  // signal so a thermally-driven cooling machine is not falsely blocked.
  if (ctx.wasteHeatDeclared === true) return true;
  const sources = ctx.sources ?? [];
  if (sources.some(s => ON_SITE_THERMAL_SOURCE_KEYS.has(s))) return true;
  const components = ctx.components ?? [];
  return components.some(c => ON_SITE_THERMAL_COMPONENT_KEYS.has(c));
}

/**
 * Build a {@link CoolingContext} from a project intent — collects every coarse
 * `powerSource` and every granular `powerSourceConfig.components` entry across
 * ALL effective phases (the same scope powerStack uses for the waste-heat
 * credit). `waterStressed` is passed through from the caller (the form/wizard
 * supplies it from a site water signal when one exists; absent by default).
 */
export function coolingContextFromIntent(
  intent: ProjectIntent | undefined | null,
  opts?: {
    waterStressed?: boolean;
    /**
     * P3-6 — on-site thermal-load / waste-heat EVIDENCE signal (from a SED
     * cooling / thermal-load section). When `true`, the cooling decision runs
     * off EVIDENCE, not just the intent/power-mix inference: a thermally-driven
     * machine / absorption sink stays viable. The caller passes this from the
     * SED (e.g. a confirmed thermal-load > 0 or a waste-heat offtaker section).
     */
    wasteHeatEvidenced?: boolean;
  },
): CoolingContext {
  const phases: ReadonlyArray<ProjectPhase> = intent
    ? effectivePhases(intent).phases
    : [];
  const sources: PowerSourceKey[] = [];
  const components: PhasePowerComponentKey[] = [];
  for (const phase of phases) {
    if (phase.powerSource && !sources.includes(phase.powerSource)) {
      sources.push(phase.powerSource);
    }
    for (const c of phase.powerSourceConfig?.components ?? []) {
      if (!components.includes(c)) components.push(c);
    }
  }
  // P3-6 — resolve a DECLARED waste-heat offtake from intent: an `LOI` or
  // `contracted` offtake, or any declared offtaker / usable thermal load, is a
  // real thermal-load signal (only `none`/undecided/`exploring`-without-detail
  // carry no signal). Combined (OR) with any evidence signal the caller passes.
  const offtake = intent?.wasteHeatOfftake;
  const declaredWasteHeat = Boolean(
    offtake
    && offtake.status !== 'none'
    && (
      offtake.status === 'LOI'
      || offtake.status === 'contracted'
      || (offtake.offtaker?.trim().length ?? 0) > 0
      || (Number.isFinite(offtake.thermalLoadMwth) && (offtake.thermalLoadMwth ?? 0) > 0)
    ),
  );
  const wasteHeatDeclared = declaredWasteHeat || opts?.wasteHeatEvidenced === true;

  // Thread the sibling cooling axes so the cross-field consistency gate can
  // disable the contradictory pair (thermal `absorption-cchp` sink + electric
  // machine, or vice-versa). These are intent-LEVEL; per-phase overrides are
  // resolved by the caller when it builds a per-phase context.
  return {
    sources,
    components,
    waterStressed: opts?.waterStressed,
    coolingGeneration: intent?.coolingGeneration,
    bopHeatRejection: intent?.bopHeatRejection,
    ...(wasteHeatDeclared ? { wasteHeatDeclared: true } : {}),
  };
}

/** The result of any single viability check. */
export interface CoolingViability {
  /** Whether the option is physically viable for the given context. */
  viable: boolean;
  /** Human-readable reason WHY it is infeasible (present only when relevant). */
  reason?: string;
  /**
   * `true` when the option is viable but ADVISORY-flagged (e.g. water-intensive
   * rejection with no water signal). Lets a UI render a soft note WITHOUT
   * disabling the option. Always paired with a `reason`. Never set when
   * `viable === false` (a hard block is not a warning).
   */
  warn?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// VALIDITY MATRIX (declarative) — axis × value → rule.
//
// COOLING GENERATION (the MACHINE that produces the cooling effect):
//   absorption-thermal ............ THERMALLY-DRIVEN  → requires waste heat.
//   steam-driven-mechanical ....... THERMALLY-DRIVEN  → requires waste heat.
//   vapour-compression-electric ... electric chiller  → ALWAYS viable.
//   free-cooling-economiser ....... ambient/economiser→ ALWAYS viable.
//   none .......................... no machine        → ALWAYS viable.
//   unknown ....................... not yet decided    → ALWAYS viable.
//
// HEAT REJECTION (the cold-END sink):
//   absorption-cchp ............... LEGACY thermal sink (back-maps to
//                                   absorption-thermal) → requires waste heat.
//   wet-cooling-tower ............. evaporative        → viable; WARN if no
//                                   water signal; hard-block only on water
//                                   stress.
//   once-through .................. river/sea          → viable; WARN if no
//                                   water signal; hard-block only on water
//                                   stress.
//   dry-cooler / hybrid /
//   free-cooling / unknown ........ no thermal/water dependency → ALWAYS viable.
//
// IT-SIDE COOLING STRATEGY (air / liquid / hybrid / undecided): the rack-side
//   heat-CAPTURE loop carries no power-mix or water dependency → ALWAYS viable.
//   (Listed for completeness; gating it is a non-goal — it is orthogonal.)
// ─────────────────────────────────────────────────────────────────────────────

/** Cooling-GENERATION machines that are THERMALLY DRIVEN (need waste heat). */
const THERMALLY_DRIVEN_GENERATION: ReadonlySet<CoolingGeneration> = new Set([
  'absorption-thermal',
  'steam-driven-mechanical',
]);

/** Heat-rejection SINKS that are THERMALLY DRIVEN (need waste heat). The legacy
 *  `absorption-cchp` value smuggled the absorption MACHINE into the sink enum
 *  (powerStack back-maps it to `absorption-thermal`), so it carries the same
 *  waste-heat requirement. */
const THERMALLY_DRIVEN_SINKS: ReadonlySet<BopHeatRejection> = new Set([
  'absorption-cchp',
]);

/** Heat-rejection SINKS that consume water at scale (evaporative / once-through). */
const WATER_INTENSIVE_SINKS: ReadonlySet<BopHeatRejection> = new Set([
  'wet-cooling-tower',
  'once-through',
]);

/**
 * Viability of a BTM COOLING-GENERATION machine for a power mix.
 *
 * Thermally-driven machines (`absorption-thermal`, `steam-driven-mechanical`)
 * require {@link wasteHeatAvailable}. Everything else is always viable.
 */
export function isCoolingGenerationViable(
  gen: CoolingGeneration,
  ctx: CoolingContext | undefined,
): CoolingViability {
  if (THERMALLY_DRIVEN_GENERATION.has(gen) && !wasteHeatAvailable(ctx)) {
    return { viable: false, reason: NO_WASTE_HEAT_REASON };
  }
  // CROSS-FIELD CONSISTENCY (additive): when the sibling cold-END sink is the
  // thermal `absorption-cchp`, only a thermal MACHINE can coexist with it — an
  // electric / free-cooling / none machine would contradict the sink. Disable
  // the conflicting machine so the impossible pair can never be selected. No-op
  // when the sibling sink is absent (partial-intent callers unaffected).
  if (ctx?.bopHeatRejection && isCoolingPairContradictory(ctx.bopHeatRejection, gen)) {
    return { viable: false, reason: CONTRADICTORY_MACHINE_REASON };
  }
  return { viable: true };
}

/**
 * Viability of a HEAT-REJECTION sink for a power mix + water signal.
 *
 * Thermally-driven sinks (legacy `absorption-cchp`) require
 * {@link wasteHeatAvailable}. Water-intensive sinks (`wet-cooling-tower`,
 * `once-through`) stay VIABLE but are WARNED when no water signal is present;
 * they are hard-blocked ONLY when a clear water-stress signal exists. All other
 * sinks are always viable.
 */
export function isHeatRejectionViable(
  sink: BopHeatRejection,
  ctx: CoolingContext | undefined,
): CoolingViability {
  if (THERMALLY_DRIVEN_SINKS.has(sink) && !wasteHeatAvailable(ctx)) {
    return { viable: false, reason: NO_WASTE_HEAT_REASON };
  }
  // CROSS-FIELD CONSISTENCY (additive): the thermal `absorption-cchp` SINK is
  // impossible when the sibling cold-production MACHINE is electric / non-thermal
  // (the sink IS the absorption machine — it cannot pair with an electric
  // chiller). Disable it so the contradiction is structurally unreachable. No-op
  // when the sibling machine is absent.
  if (ctx?.coolingGeneration && isCoolingPairContradictory(sink, ctx.coolingGeneration)) {
    return { viable: false, reason: CONTRADICTORY_SINK_REASON };
  }
  if (WATER_INTENSIVE_SINKS.has(sink)) {
    // Hard-block ONLY on a CLEAR water-stress signal; otherwise advise but allow
    // (water signal is usually absent — default is do-not-block).
    if (ctx?.waterStressed === true) {
      return { viable: false, reason: WATER_STRESS_REASON };
    }
    if (ctx?.waterStressed === undefined) {
      return { viable: true, warn: true, reason: WATER_WARN_REASON };
    }
  }
  return { viable: true };
}

/**
 * Viability of an IT-side cooling STRATEGY (air / liquid / hybrid / undecided).
 * The rack-side heat-capture loop is orthogonal to the power mix and water — it
 * is ALWAYS viable. Provided for a uniform `coolingOptionsFor('strategy', …)`
 * API; gating it is a non-goal.
 */
export function isCoolingStrategyViable(
  _strategy: NonNullable<ProjectIntent['coolingStrategy']>,
  _ctx: CoolingContext | undefined,
): CoolingViability {
  return { viable: true };
}

/** The selectable axes a UI can request options for. */
export type CoolingAxis = 'generation' | 'heat-rejection' | 'strategy';

/** A UI-ready option: value, label, and (when unavailable) a disabled flag +
 *  reason. `warn`/`warnReason` carry an advisory note for a VIABLE-but-flagged
 *  option (e.g. water-intensive rejection with no water signal). */
export interface CoolingOption {
  value: string;
  label: string;
  disabled: boolean;
  disabledReason?: string;
  warn?: boolean;
  warnReason?: string;
}

// Label maps live with the preference UI; import them so option labels match the
// rest of the app's vocabulary (single source of truth for human-readable text).
import {
  COOLING_GENERATION_LABELS,
  HEAT_REJECTION_LABELS,
} from '../reportPreferences/labels';

/** IT-side cooling-strategy labels (mirrors ProjectIntentForm's COOLING_STRATEGY_LABELS). */
const COOLING_STRATEGY_LABELS: Record<NonNullable<ProjectIntent['coolingStrategy']>, string> = {
  air: 'Air',
  liquid: 'Liquid',
  hybrid: 'Hybrid',
  undecided: 'Undecided',
};

/** Heat-rejection SINK labels — single source of truth in reportPreferences/labels.ts
 *  (imported above). The cold-END sink is "where rejected heat GOES".
 *  `absorption-cchp` is the legacy thermal-MACHINE choice carried on this axis —
 *  labelled to read as the absorption-machine cold end it actually is, so it is
 *  unmistakable against the genuinely-distinct dry / wet / once-through sinks. */

/**
 * Stable display ORDER per axis — keeps the gated lists in the same order the
 * hand-written selects used (most→least common, thermal/free/unknown last).
 */
const GENERATION_ORDER: ReadonlyArray<CoolingGeneration> = [
  'absorption-thermal',
  'vapour-compression-electric',
  'steam-driven-mechanical',
  'free-cooling-economiser',
  'none',
  'unknown',
];
const HEAT_REJECTION_ORDER: ReadonlyArray<BopHeatRejection> = [
  'dry-cooler',
  'wet-cooling-tower',
  'hybrid',
  'once-through',
  'absorption-cchp',
  'free-cooling',
  'unknown',
];
const STRATEGY_ORDER: ReadonlyArray<NonNullable<ProjectIntent['coolingStrategy']>> = [
  'air',
  'liquid',
  'hybrid',
  'undecided',
];

function toOption(value: string, label: string, v: CoolingViability): CoolingOption {
  return {
    value,
    label,
    disabled: !v.viable,
    disabledReason: v.viable ? undefined : v.reason,
    warn: v.viable && v.warn === true ? true : undefined,
    warnReason: v.viable && v.warn === true ? v.reason : undefined,
  };
}

/**
 * The full, ORDERED option list for an axis, each annotated with `disabled` +
 * `disabledReason` (hard-block) or `warn` + `warnReason` (advisory). UIs render
 * EVERY option (so the user sees why a choice is unavailable) and disable the
 * non-viable ones uniformly.
 */
export function coolingOptionsFor(
  axis: CoolingAxis,
  ctx: CoolingContext | undefined,
): CoolingOption[] {
  switch (axis) {
    case 'generation':
      return GENERATION_ORDER.map(value =>
        toOption(value, COOLING_GENERATION_LABELS[value], isCoolingGenerationViable(value, ctx)),
      );
    case 'heat-rejection':
      return HEAT_REJECTION_ORDER.map(value =>
        toOption(value, HEAT_REJECTION_LABELS[value], isHeatRejectionViable(value, ctx)),
      );
    case 'strategy':
      return STRATEGY_ORDER.map(value =>
        toOption(value, COOLING_STRATEGY_LABELS[value], isCoolingStrategyViable(value, ctx)),
      );
    default: {
      // Exhaustiveness guard — a new axis must be handled explicitly.
      const _never: never = axis;
      return _never;
    }
  }
}
